// Sync Service for Background Outbox Synchronization
import outbox from './outbox';
import voidOutbox from './voidOutbox';
import loginOutbox from './loginOutbox';
import connectivityMonitor from './connectivityMonitor';
import { Storage } from './Storage';
import axios from 'axios';
import { generateUUID } from '@/utils/uuid';
import store from '@/store';
// docs/FRONTEND.md § Sales-write user field — #26328 / Sentry 1V+1E
import { assertSalesWriteUser } from './salesWriteUser';
import {
    scheduleInvoiceSnapshot,
    scheduleTableSnapshot,
    pushInvoiceSnapshotNow
} from './deviceOfflineStatusService';

const buildStockDeltasFromItems = (items = []) => {
    const source = Array.isArray(items) ? items : [];
    const byPlu = new Map();

    for (const line of source) {
        const plu = String(line?.Plu ?? line?.plu ?? line?.PLU ?? '').trim();
        if (!plu) continue;

        const qty = Number(line?.Qty ?? line?.qty ?? line?.QTY ?? line?.Quantity ?? line?.quantity);
        if (!Number.isFinite(qty) || qty <= 0) continue;

        const key = plu.toLowerCase();
        const prev = byPlu.get(key);
        if (prev) {
            prev.qtyDelta = Number(prev.qtyDelta) + qty;
        } else {
            byPlu.set(key, { plu, qtyDelta: qty });
        }
    }

    return Array.from(byPlu.values());
};

class SyncService {
    constructor() {
        this.isSyncing = false;
        this.syncInterval = null;
        this.retryDelays = [300000]; // 5 minutes
        this.currentRetryIndex = 0;
        this.listeners = [];
        this.started = false;
        this.unsubscribeOnline = null;
        this.lastBackgroundStockSyncAt = 0;
        this.backgroundStockSyncMinIntervalMs = 15 * 60 * 1000; // 15 minutes
    }

    /**
     * Start the sync service
     * Begins periodic sync attempts when online
     */
    start() {
        if (this.started) {
            return;
        }
        this.started = true;

        // Listen to connectivity changes
        this.unsubscribeOnline = connectivityMonitor.subscribe('online', () => {
            console.log('[SyncService] Network is online. Sync will occur on the next interval.');
            // this.triggerSync(); // Removed to avoid immediate sync
            // Permanent-failed rows never enter getPending, so reconnect must still refresh
            // this PC's deviceOfflineStatus row for cashup on other tills.
            pushInvoiceSnapshotNow().catch(() => null);
        });

        // Initial sync if online
        if (connectivityMonitor.getStatus()) {
            this.triggerSync();
        }

        // Start periodic sync timer
        this._startPeriodicSync();
    }

    /**
     * Stop the sync service
     */
    stop() {
        console.log('[SyncService] Stopping background sync service');
        if (this.syncInterval) {
            clearInterval(this.syncInterval);
            this.syncInterval = null;
        }
        if (this.unsubscribeOnline) {
            this.unsubscribeOnline();
            this.unsubscribeOnline = null;
        }
        this.started = false;
    }

    /**
     * Start periodic sync with adaptive backoff
     */
    _startPeriodicSync() {
        if (this.syncInterval) {
            clearInterval(this.syncInterval);
        }

        // Start with shorter interval, will adapt based on success/failure
        const interval = this.retryDelays[this.currentRetryIndex] || 300000;

        this.syncInterval = setInterval(() => {
            if (connectivityMonitor.getStatus() && !this.isSyncing) {
                this.triggerSync();
            }
        }, interval);
    }

    /**
     * Trigger immediate sync
     * @returns {Promise<Object>} - Sync results
     */
    async triggerSync() {
        if (this.isSyncing) {
            console.log('[SyncService] Sync already in progress, skipping');
            return { skipped: true };
        }

        if (!connectivityMonitor.getStatus()) {
            console.log('[SyncService] Offline, skipping sync');
            return { offline: true };
        }

        this.isSyncing = true;

        try {
            const result = await this._syncBatch();

            // Reset retry index on success
            if (result.synced > 0) {
                this.currentRetryIndex = 0;
                this._startPeriodicSync();
            }

            this._notifyListeners('syncComplete', result);
            return result;

        } catch (error) {
            console.error('[SyncService] Sync error:', error);

            // Increase retry delay on failure
            this.currentRetryIndex = Math.min(
                this.currentRetryIndex + 1,
                this.retryDelays.length - 1
            );
            this._startPeriodicSync();

            this._notifyListeners('syncError', { error });
            return { error: error.message };

        } finally {
            // Zero pending (or only permanent failed) never hits _syncItem finally;
            // still publish backlog so this PC's snapshot stays current while online.
            scheduleInvoiceSnapshot();
            this.isSyncing = false;
        }
    }

    /**
     * Sync a single item by its saleId
     * @param {String} saleId - The saleId of the invoice to sync
     * @returns {Promise<Object>} - Sync result
     */
    async syncItemById(saleId) {
        // Wait for any ongoing batch sync to complete (up to 3 seconds)
        let waitCount = 0;
        while (this.isSyncing && waitCount < 30) {
            await new Promise(resolve => setTimeout(resolve, 100));
            waitCount++;
        }

        if (this.isSyncing) {
            console.log('[SyncService] Sync still in progress after timeout, skipping single item sync');
            return { skipped: true, reason: 'Batch sync in progress' };
        }

        if (!connectivityMonitor.getStatus()) {
            console.log('[SyncService] Offline, skipping sync');
            return { offline: true };
        }

        const allItems = await outbox.getAllOutboxItems();
        const item = allItems.find(i => i.saleId === saleId);

        if (!item) {
            console.error(`[SyncService] Item with saleId ${saleId} not found for single sync.`);
            return { error: 'Item not found' };
        }

        this.isSyncing = true;
        let result;
        try {
            // Customer-first: ensure offline customers are created before syncing this invoice
            try {
                console.log('[SyncService] Single-item sync: running customer-first sync...');
                await store.dispatch('syncOfflineDebtors');
            } catch (e) {
                console.warn('[SyncService] Single-item sync: customer sync failed, continuing with invoice', e);
            }

            await this._syncItem(item);
            result = { saleId: item.saleId, status: 'synced' };
            this._notifyListeners('itemSynced', {
                saleId: item.saleId,
                clientSaleId: item.payload.clientSaleId
            });
        } catch (error) {
            result = { saleId: item.saleId, status: 'failed', error: error.message };
            this._notifyListeners('syncError', { error });
            console.error(`[SyncService] Failed to sync item ${saleId}:`, error);
        } finally {
            this.isSyncing = false;
        }
        return result;
    }

    /**
     * Sync a batch of pending sales, voids, and logins
     * @param {Number} batchSize - Number of items to sync
     * @returns {Promise<Object>} - Results
     */
    async _syncBatch(batchSize = 10) {
        // Sync sales
        const salesResults = await this._syncSalesBatch(batchSize);

        // Sync voids
        const voidsResults = await this._syncVoidBatch(batchSize);

        // Sync logins
        const loginsResults = await this._syncLoginBatch(batchSize);

        // Sync stock checkpoint/delta in the same scheduler window.
        // Throttle this step to avoid periodic UI freezes on large stock datasets.
        let stockResult = { synced: false, mode: 'throttled' };
        const now = Date.now();
        const canRunBackgroundStockSync =
            !this.lastBackgroundStockSyncAt ||
            (now - this.lastBackgroundStockSyncAt) >= this.backgroundStockSyncMinIntervalMs;

        if (canRunBackgroundStockSync) {
            try {
                await store.dispatch('getAllStock', {
                    background: true,
                    minIntervalMs: this.backgroundStockSyncMinIntervalMs
                });
                this.lastBackgroundStockSyncAt = Date.now();
                stockResult = { synced: true, mode: 'delta-or-full' };
            } catch (stockError) {
                console.warn('[SyncService] Stock sync step failed:', stockError);
                stockResult = { synced: false, mode: 'failed', error: stockError?.message || String(stockError) };
            }
        }

        return {
            synced: salesResults.synced + voidsResults.synced + loginsResults.synced,
            failed: salesResults.failed + voidsResults.failed + loginsResults.failed,
            total: salesResults.total + voidsResults.total + loginsResults.total,
            sales: salesResults,
            voids: voidsResults,
            logins: loginsResults,
            stock: stockResult
        };
    }

    /**
     * Sync a batch of pending sales
     * @param {Number} batchSize - Number of items to sync
     * @returns {Promise<Object>} - Results
     */
    async _syncSalesBatch(batchSize = 10) {
        // 1) Always sync customers first
        try {
            await store.dispatch('syncOfflineDebtors');
        } catch (e) {
            console.warn('[SyncService] Customer sync failed, will continue with invoices but they may still reference temp IDs', e);
        }

        const pending = await outbox.getPending(batchSize);

        if (pending.length === 0) {
            return { synced: 0, failed: 0, total: 0, details: [] };
        }

        console.log(`[SyncService] Syncing ${pending.length} pending sales`);

        const results = {
            synced: 0,
            failed: 0,
            total: pending.length,
            details: []
        };

        for (const item of pending) {
            try {
                await this._syncItem(item);
                results.synced++;
                results.details.push({ saleId: item.saleId, status: 'synced' });
            } catch (error) {
                results.failed++;
                results.details.push({
                    saleId: item.saleId,
                    status: 'failed',
                    error: error.message
                });
                console.error(`[SyncService] Failed to sync ${item.saleId}:`, error);
            }
        }

        console.log(`[SyncService] Sales batch complete: ${results.synced} synced, ${results.failed} failed`);
        return results;
    }

    /**
     * Sync a batch of pending voids
     * @param {Number} batchSize - Number of items to sync
     * @returns {Promise<Object>} - Results
     */
    async _syncVoidBatch(batchSize = 10) {
        const pending = await voidOutbox.getPendingVoids(batchSize);

        if (pending.length === 0) {
            return { synced: 0, failed: 0, total: 0, details: [] };
        }

        console.log(`[SyncService] Syncing ${pending.length} pending voids`);

        const results = {
            synced: 0,
            failed: 0,
            total: pending.length,
            details: []
        };

        for (const item of pending) {
            try {
                await this._syncVoidItem(item);
                results.synced++;
                results.details.push({ voidId: item.voidId, status: 'synced' });
            } catch (error) {
                results.failed++;
                results.details.push({
                    voidId: item.voidId,
                    status: 'failed',
                    error: error.message
                });
                console.error(`[SyncService] Failed to sync void ${item.voidId}:`, error);
            }
        }

        console.log(`[SyncService] Voids batch complete: ${results.synced} synced, ${results.failed} failed`);
        return results;
    }

    /**
     * Sync a batch of pending login records
     * @param {Number} batchSize - Number of items to sync
     * @returns {Promise<Object>} - Results
     */
    async _syncLoginBatch(batchSize = 50) {
        const pending = await loginOutbox.getPendingLogins(batchSize);

        if (pending.length === 0) {
            return { synced: 0, failed: 0, total: 0, details: [] };
        }

        console.log(`[SyncService] Syncing ${pending.length} pending login records`);

        const results = {
            synced: 0,
            failed: 0,
            total: pending.length,
            details: []
        };

        for (const item of pending) {
            try {
                await this._syncLoginItem(item);
                results.synced++;
                results.details.push({ loginId: item.id, status: 'synced' });
            } catch (error) {
                results.failed++;
                results.details.push({
                    loginId: item.id,
                    status: 'failed',
                    error: error.message
                });
                console.error(`[SyncService] Failed to sync login ${item.id}:`, error);
            }
        }

        console.log(`[SyncService] Logins batch complete: ${results.synced} synced, ${results.failed} failed`);
        return results;
    }

    /**
     * Sync a single item
     * @param {Object} item - Outbox item
     * @returns {Promise<void>}
     */
    async _syncItem(item) {
        // Mark as sending
        const itemStoreDb = item.storeDB || item.storeDb || '';
        await outbox.markSending(item.saleId, itemStoreDb);

        try {
            // Get store info
            const store = await Storage.getDefaultStore();

            // Post to server using the same endpoint and configuration as online sales (sales-module.js line 611)
            // Important: Match exact pattern to avoid CORS preflight requests
            const url = serverApi + `invoice/${item.storeDB}/mobile?android=true`;

            console.log(`[SyncService] Syncing ${item.saleId} to: ${url}`);

            // CRITICAL: Use the original localId for proper idempotency
            // DO NOT regenerate - the same localId must be used across all retry attempts
            // to allow the server to detect and reject duplicate submissions
            console.log(`[SyncService] Syncing offline sale ${item.saleId} with original localId ${item.payload.localId}`);

            const payload = {
                ...item.payload,
                localId: item.payload.localId, // Preserve original localId for idempotency
                offlineSaleNumber: item.saleId,
                saleDate: this.formatDateForSync(item.date)
            };
            // Fail-fast if outbox invoice lost cashier identity (#26328)
            assertSalesWriteUser(payload.user || payload.User, 'syncService._syncItem.invoiceMobile');

            // Remap customer temp IDs to real IDs if mapping exists
            try {
                const idMap = await Storage.getCustomerIdMap();
                if (payload && payload.customer && idMap && idMap[payload.customer]) {
                    console.log(`[SyncService] Rewriting customer ${payload.customer} -> ${idMap[payload.customer]} for sale ${item.saleId}`);
                    payload.customer = idMap[payload.customer];
                }
                if (payload && payload.LPO && payload.LPO.debtorID && idMap && idMap[payload.LPO.debtorID]) {
                    payload.LPO.debtorID = idMap[payload.LPO.debtorID];
                }
            } catch (mapErr) {
                console.warn('[SyncService] Could not apply customer ID mapping', mapErr);
            }

            // Use same config as sales-module.js: imported axios, timeout only, NO explicit headers
            // Explicit headers can trigger CORS preflight OPTIONS requests
            // Timeout increased to 20s to match sales-module.js and reduce false timeouts
            const { data } = await axios.post(url, payload, {
                timeout: 20000
            });

            // Parse response if needed
            let parsedData = data;
            if (typeof data === 'string') {
                const correctedData = data.replaceAll(/NaN/g, '0');
                parsedData = JSON.parse(correctedData);
            }

            // Check if successful
            if (parsedData.status === 200 && parsedData.response) {
                // Mark as sent with server metadata
                await outbox.markSent(item.saleId, {
                    invoiceNo: parsedData.response.invoice,
                    serverInvoiceId: parsedData.response.invoiceId,
                    postedAt: new Date().toISOString()
                }, itemStoreDb);

                // Keep dirty tracking until server stock catches up; only release pending counter here.
                try {
                    const saleDeltas = buildStockDeltasFromItems(item?.payload?.items);
                    if (saleDeltas.length > 0) {
                        await Storage.releaseStockDirtyPending(saleDeltas, item.saleId);
                    }
                    await Storage.invalidateStockCache();
                } catch (dirtyError) {
                    console.warn(`[SyncService] Failed to release dirty stock flags for ${item.saleId}:`, dirtyError);
                }

                console.log(`[SyncService] Successfully synced ${item.saleId} -> Invoice ${parsedData.response.invoice}`);

                // Notify listeners
                this._notifyListeners('itemSynced', {
                    saleId: item.saleId,
                    invoiceNo: parsedData.response.invoice,
                    clientSaleId: item.payload.clientSaleId
                });

            } else {
                // Non-200 status code - mark as permanently failed with error details
                const errorMessage = parsedData.message || parsedData.error || 'Server returned non-200 status';
                const errorDetails = {
                    reason: 'Server rejected invoice',
                    statusCode: parsedData.status,
                    errorMessage: errorMessage,
                    serverResponse: parsedData,
                    failedAt: new Date().toISOString()
                };
                await outbox.markFailed(item.saleId, { error: errorDetails }, true, itemStoreDb); // permanent=true to prevent retrying

                console.error(`[SyncService] Server rejected ${item.saleId} with status ${parsedData.status}: ${errorMessage}`);

                // Throw error to prevent retrying
                throw new Error(`Server rejected invoice with status ${parsedData.status}: ${errorMessage}`);
            }

        } catch (error) {
            // Determine if error is retriable
            const isRetriable = this._isRetriableError(error);
            const itemStoreDb = item.storeDB || item.storeDb || '';

            if (isRetriable) {
                // Check current attempts
                const currentAttempts = item.numberOfAttempts || 0;

                if (currentAttempts >= 5) {
                    // Too many retries, mark as failed (will become timeout in markFailed)
                    const errorDetails = {
                        reason: 'Max retries exceeded',
                        lastError: error.message,
                        attempts: currentAttempts
                    };
                    await outbox.markFailed(item.saleId, { error: errorDetails }, false, itemStoreDb);
                } else {
                    // Retry later
                    await outbox.incrementRetries(item.saleId, itemStoreDb);
                }
            } else {
                // Non-retriable error (validation, etc.)
                const errorDetails = {
                    reason: 'Non-retriable error',
                    error: error.response?.data || error.message
                };
                await outbox.markFailed(item.saleId, { error: errorDetails }, true, itemStoreDb); // permanent=true to prevent retrying
            }

            throw error;
        } finally {
            // Record offline backlog stats after every invoice sync attempt
            scheduleInvoiceSnapshot();
        }
    }

    /**
     * Sync a single void item
     * @param {Object} item - Void outbox item
     * @returns {Promise<void>}
     */
    async _syncVoidItem(item) {
        // Mark as sending
        await voidOutbox.markVoidSending(item.voidId);

        try {
            // Get store info
            const store = await Storage.getDefaultStore();

            // Post to server using the same endpoint as logItemVoid
            const url = serverApi + `supervisorOverrides/${item.storeDB}/logItemVoid`;

            console.log(`[SyncService] Syncing void ${item.voidId} to: ${url}`);

            // Fail-fast if outbox void lost cashier identity (#26328)
            assertSalesWriteUser(item.payload?.User || item.payload?.user, 'syncService._syncVoidItem.logItemVoid');

            // Post void data
            await axios.post(url, item.payload, {
                timeout: 10000
            });

            // Mark as sent
            await voidOutbox.markVoidSent(item.voidId, {
                postedAt: new Date().toISOString()
            });

            console.log(`[SyncService] Successfully synced void ${item.voidId}`);

            // Notify listeners
            this._notifyListeners('voidSynced', {
                voidId: item.voidId
            });

        } catch (error) {
            // Determine if error is retriable
            const isRetriable = this._isRetriableError(error);

            if (isRetriable) {
                // Check current attempts
                const currentAttempts = item.numberOfAttempts || 0;

                if (currentAttempts >= 5) {
                    // Too many retries, mark as failed
                    await voidOutbox.markVoidFailed(item.voidId, {
                        reason: 'Max retries exceeded',
                        lastError: error.message,
                        attempts: currentAttempts
                    });
                } else {
                    // Retry later
                    await voidOutbox.incrementVoidRetries(item.voidId);
                }
            } else {
                // Non-retriable error
                await voidOutbox.markVoidFailed(item.voidId, {
                    reason: 'Non-retriable error',
                    error: error.response?.data || error.message
                });
            }

            throw error;
        } finally {
            scheduleTableSnapshot();
        }
    }

    /**
     * Sync a single login record
     * @param {Object} item - Login outbox item
     * @returns {Promise<void>}
     */
    async _syncLoginItem(item) {
        // Mark as sending
        await loginOutbox.markSending(item.id);

        try {
            // Ensure we have a storeDB
            let storeDB = item.storeDB;
            if (!storeDB) {
                try {
                    const store = await Storage.getDefaultStore();
                    if (store && store.StoreDB) {
                        storeDB = store.StoreDB;
                        console.log(`[SyncService] Recovered storeDB ${storeDB} for login ${item.id}`);
                    }
                } catch (e) {
                    console.warn('[SyncService] Failed to recover storeDB for login sync', e);
                }
            }

            if (!storeDB) {
                throw new Error('Missing storeDB for login sync');
            }

            // Post to server using the same endpoint as recordlogin
            const url = serverApi + `supervisorOverrides/${storeDB}/recordLogin`;

            console.log(`[SyncService] Syncing login ${item.id} to: ${url}`);

            const payload = {
                // Fail-fast if outbox login lost username (#26328)
                username: assertSalesWriteUser(item.username, 'syncService._syncLoginItem.recordLogin'),
                platform: item.platform,
                pc: item.pc
            };

            // Post login data
            await axios.post(url, payload, {
                timeout: 10000
            });

            // Mark as sent
            await loginOutbox.markSent(item.id);

            console.log(`[SyncService] Successfully synced login ${item.id}`);

            // Notify listeners
            this._notifyListeners('loginSynced', {
                loginId: item.id,
                username: item.username
            });

        } catch (error) {
            // Determine if error is retriable
            const isRetriable = this._isRetriableError(error);

            if (isRetriable) {
                // Check current attempts
                const currentAttempts = item.attempts || 0;

                if (currentAttempts >= 5) {
                    // Too many retries, mark as failed
                    await loginOutbox.markFailed(item.id, true);
                    console.log(`[SyncService] Login ${item.id} marked as failed after max retries`);
                } else {
                    // Retry later
                    await loginOutbox.incrementRetries(item.id);
                    console.log(`[SyncService] Login ${item.id} will be retried (attempt ${currentAttempts + 1})`);
                }
            } else {
                // Non-retriable error
                await loginOutbox.markFailed(item.id, true);
                console.log(`[SyncService] Login ${item.id} marked as permanently failed`);
            }

            throw error;
        }
    }

    /**
     * Determine if an error is retriable
     * @param {Error} error - Error object
     * @returns {Boolean}
     */
    _isRetriableError(error) {
        // Server rejected with non-200 status - not retriable
        if (error.message && error.message.includes('Server rejected invoice')) {
            return false;
        }

        // Network errors are retriable
        if (!error.response) {
            return true;
        }

        // Server errors (5xx) are retriable
        if (error.response.status >= 500) {
            return true;
        }

        // Timeout errors are retriable
        if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
            return true;
        }

        // Client errors (4xx) are generally not retriable
        return false;
    }

    /**
     * Get sync status
     * @returns {Promise<Object>} - Status info
     */
    async getStatus() {
        // Sales counts
        const salesPendingCount = await outbox.countByStatus('pending');
        const salesSendingCount = await outbox.countByStatus('sending');
        const salesSentCount = await outbox.countByStatus('sent');
        const salesFailedCount = await outbox.countByStatus('failed');

        // Void counts
        const voidsPendingCount = await voidOutbox.countVoidsByStatus('pending');
        const voidsSendingCount = await voidOutbox.countVoidsByStatus('sending');
        const voidsSentCount = await voidOutbox.countVoidsByStatus('sent');
        const voidsFailedCount = await voidOutbox.countVoidsByStatus('failed');

        return {
            pending: salesPendingCount + voidsPendingCount,
            sending: salesSendingCount + voidsSendingCount,
            sent: salesSentCount + voidsSentCount,
            failed: salesFailedCount + voidsFailedCount,
            sales: {
                pending: salesPendingCount,
                sending: salesSendingCount,
                sent: salesSentCount,
                failed: salesFailedCount
            },
            voids: {
                pending: voidsPendingCount,
                sending: voidsSendingCount,
                sent: voidsSentCount,
                failed: voidsFailedCount
            },
            isSyncing: this.isSyncing,
            isOnline: connectivityMonitor.getStatus()
        };
    }

    /**
     * Subscribe to sync events
     * @param {String} event - Event name ('syncComplete', 'syncError', 'itemSynced')
     * @param {Function} callback - Callback function
     * @returns {Function} - Unsubscribe function
     */
    subscribe(event, callback) {
        const listener = { event, callback };
        this.listeners.push(listener);

        return () => {
            const index = this.listeners.indexOf(listener);
            if (index > -1) {
                this.listeners.splice(index, 1);
            }
        };
    }

    /**
     * Notify listeners
     * @param {String} event - Event name
     * @param {Object} data - Event data
     */
    _notifyListeners(event, data) {
        this.listeners.forEach(listener => {
            if (listener.event === event || listener.event === 'all') {
                try {
                    listener.callback(data);
                } catch (error) {
                    console.error('Error in sync listener:', error);
                }
            }
        });
    }

    /**
     * Manual retry of failed items
     * @returns {Promise<Object>} - Retry results
     */
    async retryFailed() {
        console.log('[SyncService] Manually retrying failed items');

        // Get all failed items
        const allItems = await outbox.getAllOutboxItems();
        const failedItems = allItems.filter(item => item.status === 'failed');

        if (failedItems.length === 0) {
            return { message: 'No failed items to retry' };
        }

        // Reset them to pending
        for (const item of failedItems) {
            await outbox.incrementRetries(item.saleId, item.storeDB || item.storeDb);
        }

        // Trigger sync
        return this.triggerSync();
    }

    /**
     * Format date for sync payload
     * @param {String} dateString - Date string
     * @returns {String|null} - Formatted date string or null
     */
    formatDateForSync(dateString) {
        if (!dateString) return null;
        const date = new Date(dateString);

        const pad = (num) => num.toString().padStart(2, '0');

        const year = date.getFullYear();
        const month = pad(date.getMonth() + 1);
        const day = pad(date.getDate());
        const hours = pad(date.getHours());
        const minutes = pad(date.getMinutes());
        const seconds = pad(date.getSeconds());

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }
}

// Create singleton instance
const syncService = new SyncService();

export default syncService;
